import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { caseStudies, testimonials } from '../mock';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

const Portfolio = () => {
  const [expandedCard, setExpandedCard] = useState(null);

  const toggleCard = (id) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  const scrollToContact = () => {
    window.location.href = '/#contact';
  };

  // Testimonials carousel for mobile
  const [testimonialsEmblaRef] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 5000, stopOnInteraction: false })]
  );

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="pt-32 pb-12 px-6 lg:px-8 bg-gradient-to-br from-amber-50 to-white">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="bg-amber-100 text-amber-700 mb-4">Portfolio</Badge>
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Selected Work & Growth Outcomes
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real B2B SaaS content systems that generated traffic, rankings, and leads.
          </p>
        </div>
      </section>

      {/* Case Studies Section */}
      <section className="py-16 px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Desktop: Full Cards */}
          <div className="hidden md:block space-y-16">
            {caseStudies.map((study) => (
              <Card key={study.id} className="border-2 hover:shadow-xl transition-shadow overflow-hidden">
                {/* Case Study Header */}
                <div className="bg-gradient-to-r from-amber-50 to-white p-8 border-b">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                    <div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">{study.client}</h2>
                      <Badge className="bg-gray-100 text-gray-700">{study.period}</Badge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {study.tags.map((tag, idx) => (
                        <Badge key={idx} className="bg-amber-100 text-amber-700">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>

                <CardContent className="p-8 space-y-8">
                  {/* Challenge */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">Challenge</h3>
                    <p className="text-gray-700 leading-relaxed">{study.challenge}</p>
                  </div>

                  {/* What I Did */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">What I Did</h3>
                    <ul className="space-y-2">
                      {study.solution.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-gray-700">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Outcomes */}
                  <div className="bg-gradient-to-br from-amber-50 to-white p-6 rounded-xl">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Outcomes</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {study.outcomes.map((outcome, idx) => (
                        <div key={idx} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-amber-600 rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-gray-800 font-medium">{outcome}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile: Collapsible Cards in 2 Columns */}
          <div className="md:hidden grid grid-cols-2 gap-4">
            {caseStudies.map((study) => (
              <Card
                key={study.id}
                className="border-2 hover:shadow-lg transition-all cursor-pointer"
                onClick={() => toggleCard(study.id)}
              >
                <CardContent className="p-4">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{study.client}</h3>
                  <p className="text-xs text-gray-600 mb-3">{study.period}</p>
                  
                  {expandedCard !== study.id ? (
                    <div>
                      <p className="text-sm text-gray-700 line-clamp-3 mb-3">{study.challenge}</p>
                      <div className="flex items-center text-amber-600 text-sm font-medium">
                        <span>Read more</span>
                        <ChevronDown size={16} className="ml-1" />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-2">Challenge</h4>
                        <p className="text-sm text-gray-700">{study.challenge}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-sm font-bold text-gray-900 mb-2">What I Did</h4>
                        <ul className="space-y-1">
                          {study.solution.slice(0, 3).map((item, idx) => (
                            <li key={idx} className="text-xs text-gray-700 flex items-start">
                              <span className="text-amber-600 mr-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="bg-amber-50 p-3 rounded-lg">
                        <h4 className="text-sm font-bold text-gray-900 mb-2">Key Results</h4>
                        <ul className="space-y-1">
                          {study.outcomes.slice(0, 2).map((outcome, idx) => (
                            <li key={idx} className="text-xs text-gray-800 font-medium flex items-start">
                              <span className="text-amber-600 mr-1">•</span>
                              <span>{outcome}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="flex items-center text-amber-600 text-sm font-medium">
                        <span>Show less</span>
                        <ChevronUp size={16} className="ml-1" />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Client Testimonials</h2>
          
          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Users className="text-amber-600" size={24} />
                    </div>
                    <div>
                      <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                      <div>
                        <div className="font-bold text-gray-900">{testimonial.name}</div>
                        <div className="text-sm text-gray-600">{testimonial.role}</div>
                        {testimonial.company && (
                          <div className="text-sm text-amber-600 font-medium">{testimonial.company}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile: Carousel */}
          <div className="md:hidden overflow-hidden" ref={testimonialsEmblaRef}>
            <div className="flex">
              {testimonials.map((testimonial) => (
                <div key={testimonial.id} className="flex-[0_0_100%] min-w-0 px-4">
                  <Card className="border-2">
                    <CardContent className="pt-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Users className="text-amber-600" size={24} />
                        </div>
                        <div>
                          <p className="text-gray-700 mb-4 italic text-sm">"{testimonial.content}"</p>
                          <div>
                            <div className="font-bold text-gray-900">{testimonial.name}</div>
                            <div className="text-sm text-gray-600">{testimonial.role}</div>
                            {testimonial.company && (
                              <div className="text-sm text-amber-600 font-medium">{testimonial.company}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 lg:px-8 bg-gradient-to-br from-amber-600 to-amber-700 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Want similar results for your SaaS content?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Let's build a content engine that generates qualified leads for your B2B SaaS.
          </p>
          <Button
            size="lg"
            onClick={scrollToContact}
            className="bg-white text-amber-600 hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all"
          >
            Hire Me <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
